import { format, formatDistanceToNow } from "date-fns";

import { es } from "date-fns/locale";

import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import TimeSeriesChartCard from "../../../shared/components/charts/TimeSeriesChartCard";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import { StatsCard } from "../../stats/components/subcomponents";

import { useSecurityOverviewQuery } from "../hooks/useSecurity";

import type { SecurityRange, ZapRisk } from "../types";

const RANGE_OPTIONS: { value: SecurityRange; label: string }[] = [
  { value: "day", label: "1 día" },

  { value: "week", label: "1 semana" },

  { value: "month", label: "1 mes" },
];

const blockedPalette = {
  border: "rgb(239, 68, 68)",

  background: "rgba(239, 68, 68, 0.18)",

  point: "rgb(248, 113, 113)",
} as const;

const alertsPalette = {
  border: "rgb(245, 158, 11)",

  background: "rgba(245, 158, 11, 0.18)",

  point: "rgb(251, 191, 36)",
} as const;

const formatNumber = (value: number) => value.toLocaleString("es-AR");

// Etiqueta del eje X según el rango: en 1 día basta la hora; en rangos largos

// el punto sería ambiguo con solo la hora, así que se muestra el día.

const formatSeriesLabel = (iso: string, range: SecurityRange) => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return iso;

  if (range === "day") return format(date, "HH:mm", { locale: es });

  if (range === "week") return format(date, "EEE HH'h'", { locale: es });

  return format(date, "dd/MM", { locale: es });
};

const formatRelative = (iso: string) => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return iso;

  return formatDistanceToNow(date, { addSuffix: true, locale: es });
};

const formatDateTime = (iso: string) => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return iso;

  return format(date, "dd/MM/yy HH:mm", { locale: es });
};

const hostOf = (target: string) => {
  try {
    return new URL(target).host;
  } catch {
    return target;
  }
};

const ZAP_RISK_LABELS: Record<ZapRisk, string> = {
  high: "Alta",

  medium: "Media",

  low: "Baja",

  info: "Info",
};

const ZAP_RISK_CLASSES: Record<ZapRisk, string> = {
  high: "border-transparent bg-red-500/20 text-red-300",

  medium: "border-transparent bg-amber-500/20 text-amber-300",

  low: "border-transparent bg-slate-500/20 text-slate-300",

  info: "border-transparent bg-muted text-muted-foreground",
};

const ZAP_SCAN_KIND_LABELS = {
  baseline: "Escaneo pasivo (diario)",

  full: "Escaneo activo (semanal)",
} as const;

const RiskBadge = ({ risk }: { risk: ZapRisk }) => (
  <Badge className={ZAP_RISK_CLASSES[risk]}>{ZAP_RISK_LABELS[risk]}</Badge>
);

const SecurityPage = () => {
  const [range, setRange] = useState<SecurityRange>("day");

  const rangeLabel =
    RANGE_OPTIONS.find((opt) => opt.value === range)?.label ?? "1 día";

  const { data, isLoading, isFetching, isError, refetch } =
    useSecurityOverviewQuery(range);

  const blockedSeries = useMemo(() => {
    if (!data) return [];

    return data.series.map((point) => ({
      label: formatSeriesLabel(point.t, range),

      value: point.blockedActive,
    }));
  }, [data, range]);

  const alertsSeries = useMemo(() => {
    if (!data) return [];

    return data.series.map((point) => ({
      label: formatSeriesLabel(point.t, range),

      value: point.alertsLast10m,
    }));
  }, [data, range]);

  const maxScenarioCount = useMemo(() => {
    if (!data || data.topScenarios.length === 0) return 0;

    return Math.max(...data.topScenarios.map((s) => s.count));
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Seguridad"
          subtitle="Estado del firewall CrowdSec en tiempo real."
        />
        <LoadingOverlay />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Seguridad"
          subtitle="Estado del firewall CrowdSec en tiempo real."
        />
        <div
          role="alert"
          className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-100"
        >
          <div className="font-semibold text-red-50">
            No se pudo cargar el estado de seguridad
          </div>
          <div className="mt-1 text-red-200/80">
            Revisá la conexión con el colector de CrowdSec.{" "}
            <button
              type="button"
              onClick={() => refetch()}
              className="underline underline-offset-2 hover:text-red-100"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { live, totals, stale, updatedAt, zap, degraded } = data;

  // ZAP: datos viejos ≠ ausencia de datos. Mostramos los hallazgos que haya y

  // el banner ámbar de staleness por separado.

  const zapHasData = zap.targets.length > 0;

  // Sin datos frescos no se puede afirmar "sin ataques": el banner verde solo

  // aparece con un snapshot reciente; si no, un neutro "estado desconocido".

  const liveSignalKnown = Boolean(updatedAt) && !stale;

  const cards = [
    { title: "IPs bloqueadas (total)", value: totals.blockedActive },

    { title: "Blocklist comunitaria", value: totals.community },

    { title: "Bloqueos locales", value: totals.local },

    { title: "Alertas última hora", value: live.alertsLastHour },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Seguridad"
        subtitle="Estado del firewall CrowdSec en tiempo real."
      />

      {liveSignalKnown && live.underAttack ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-red-100"
        >
          <div className="text-base font-semibold text-red-50">
            🛑 ATAQUE EN VIVO — {formatNumber(live.alertsLast10m)} alertas en
            los últimos 10 min
          </div>
        </div>
      ) : liveSignalKnown ? (
        <div
          role="status"
          className="rounded-lg border border-green-500/40 bg-green-500/10 p-4 text-sm text-green-100"
        >
          <div className="text-base font-semibold text-green-50">
            ✓ Sin ataques activos
          </div>
        </div>
      ) : (
        <div
          role="status"
          className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground"
        >
          <div className="text-base font-semibold text-foreground">
            ? Estado desconocido
          </div>
          <div className="mt-1">
            Sin datos frescos del colector: no se puede afirmar si hay o no
            ataques activos.
          </div>
        </div>
      )}

      {degraded && liveSignalKnown ? (
        <div
          role="alert"
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100"
        >
          ⚠ Colector degradado: alguna fuente (cscli / LAPI / ipset) falló en el
          último reporte — los números pueden estar incompletos.
        </div>
      ) : null}

      {stale ? (
        <div
          role="alert"
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100"
        >
          <div className="font-semibold text-amber-50">
            ⚠ Datos desactualizados (el colector no reporta hace rato)
          </div>
          <div className="mt-1 text-amber-200/80">
            {updatedAt
              ? `Último reporte: ${formatRelative(updatedAt)} (${format(
                  new Date(updatedAt),

                  "dd/MM/yy HH:mm",

                  { locale: es },
                )})`
              : "El colector nunca reportó."}
          </div>
        </div>
      ) : null}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <StatsCard key={card.title} title={card.title} value={card.value} />
        ))}
      </section>

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-neutral-200">
          Evolución en el tiempo
        </h2>
        <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-1">
          {RANGE_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              type="button"
              size="sm"
              variant={range === opt.value ? "default" : "ghost"}
              onClick={() => setRange(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <TimeSeriesChartCard
          title="IPs bloqueadas (en el tiempo)"
          points={blockedSeries}
          palette={blockedPalette}
          emptyDescription="Aún no hay registros del colector."
        />
        <TimeSeriesChartCard
          title="Alertas (últimos 10 min)"
          points={alertsSeries}
          palette={alertsPalette}
          emptyDescription="Aún no hay registros del colector."
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            Top escenarios ({rangeLabel})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.topScenarios.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              Sin escenarios registrados.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {data.topScenarios.map((scenario) => (
                <div key={scenario.scenario} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs text-foreground">
                      {scenario.scenario}
                    </span>
                    <span className="font-mono font-medium tabular-nums text-muted-foreground">
                      {formatNumber(scenario.count)}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted/40">
                    <div
                      className="h-full rounded-full bg-destructive/70"
                      style={{
                        width: `${
                          maxScenarioCount > 0
                            ? Math.max(
                                4,

                                (scenario.count / maxScenarioCount) * 100,
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            Alertas ({rangeLabel})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>IP</TableHead>
                <TableHead>Escenario</TableHead>
                <TableHead>País</TableHead>
                <TableHead>AS</TableHead>
                <TableHead className="text-right">Eventos</TableHead>
                <TableHead className="text-right">Cuándo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recentAlerts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-muted-foreground"
                  >
                    Sin alertas en el rango
                  </TableCell>
                </TableRow>
              ) : (
                data.recentAlerts.map((alert, index) => (
                  <TableRow key={`${alert.ip}-${alert.createdAt}-${index}`}>
                    <TableCell className="font-mono text-xs">
                      {alert.ip}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {alert.scenario}
                    </TableCell>
                    <TableCell>{alert.country ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {alert.as ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(alert.events)}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {formatRelative(alert.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            Decisiones locales ({rangeLabel})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              {/* Sin columna País: cscli decisions no trae geo (el colector
                  siempre mandaba null y la columna vivía en "—"). */}
              <TableRow>
                <TableHead>IP</TableHead>
                <TableHead>Escenario</TableHead>
                <TableHead>Duración</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead className="text-right">Cuándo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recentDecisions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground"
                  >
                    Sin decisiones locales en el rango
                  </TableCell>
                </TableRow>
              ) : (
                data.recentDecisions.map((decision, index) => (
                  <TableRow
                    key={`${decision.ip}-${decision.scenario}-${index}`}
                  >
                    <TableCell className="font-mono text-xs">
                      {decision.ip}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {decision.scenario}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {decision.duration}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {decision.origin}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {decision.lastSeenAt ? formatRelative(decision.lastSeenAt) : "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <section className="flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Análisis DAST — OWASP ZAP
          </h2>
          <p className="text-sm text-muted-foreground">
            Escaneo de vulnerabilidades web sobre staging.
          </p>
        </div>

        {zap.stale ? (
          <div
            role="alert"
            className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100"
          >
            <div className="font-semibold text-amber-50">
              ⚠ Sin reportes de ZAP recientes
            </div>
            <div className="mt-1 text-amber-200/80">
              {zap.updatedAt
                ? `Último escaneo: ${formatRelative(
                    zap.updatedAt,
                  )} (${formatDateTime(zap.updatedAt)}). Se muestran los
                  hallazgos de ese escaneo.`
                : "Todavía no se ejecutó ningún escaneo."}
            </div>
          </div>
        ) : null}

        {!zapHasData && !zap.stale ? (
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            El último escaneo no reportó objetivos.
          </div>
        ) : null}

        {zapHasData ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              {zap.scanKind ? (
                <Badge variant="outline">
                  {ZAP_SCAN_KIND_LABELS[zap.scanKind]}
                </Badge>
              ) : null}
              {zap.updatedAt ? (
                <span className="text-xs text-muted-foreground">
                  {formatRelative(zap.updatedAt)} (
                  {formatDateTime(zap.updatedAt)})
                </span>
              ) : null}
              <div className="flex items-center gap-2">
                <Badge className="border-transparent bg-red-500/20 text-red-300">
                  Altas {formatNumber(zap.totals.high)}
                </Badge>
                <Badge className="border-transparent bg-amber-500/20 text-amber-300">
                  Medias {formatNumber(zap.totals.medium)}
                </Badge>
                <Badge className="border-transparent bg-slate-500/20 text-slate-300">
                  Bajas {formatNumber(zap.totals.low)}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {zap.targets.map((target) => (
                <Card key={target.slug}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between gap-2 text-sm font-semibold">
                      <span className="font-mono">{hostOf(target.target)}</span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {formatRelative(target.scannedAt)}
                      </span>
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2 pt-2">
                      <Badge className="border-transparent bg-red-500/20 text-red-300">
                        Altas {formatNumber(target.counts.high)}
                      </Badge>
                      <Badge className="border-transparent bg-amber-500/20 text-amber-300">
                        Medias {formatNumber(target.counts.medium)}
                      </Badge>
                      <Badge className="border-transparent bg-slate-500/20 text-slate-300">
                        Bajas {formatNumber(target.counts.low)}
                      </Badge>
                      <Badge className="border-transparent bg-muted text-muted-foreground">
                        Info {formatNumber(target.counts.info)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-3">
                    {target.alerts.length === 0 ? (
                      <div className="py-2 text-sm text-muted-foreground">
                        Sin hallazgos en este objetivo.
                      </div>
                    ) : (
                      <ul className="flex flex-col gap-2">
                        {target.alerts.map((alert, index) => (
                          <li
                            key={`${target.slug}-${alert.name}-${index}`}
                            className="flex items-center justify-between gap-3 text-sm"
                          >
                            <span className="flex items-center gap-2">
                              <RiskBadge risk={alert.risk} />
                              <span className="text-foreground">
                                {alert.name}
                              </span>
                            </span>
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                              {formatNumber(alert.instances)} instancias
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <a
                      href={target.reportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                    >
                      Ver reporte completo
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : null}
      </section>

      {isFetching ? <LoadingOverlay /> : null}
    </div>
  );
};

export default SecurityPage;
